import { campaignDispatchMeta } from '@/features/campaigns/model/campaign-dates';
import { channelLabel } from '@/features/marketing-shared/model/channels';
import type { AgendaCampaign } from './types';
import {
  computeNextRunAt,
  formatNextRunLabel,
  isCampaignExpired,
  isNextRunInWindow,
  type AgendaWindow,
} from './next-run';

export interface AgendaCampaignRow extends AgendaCampaign {
  nextRunAt: Date | null;
  nextRunLabel: string;
  dispatchLabel: string;
}

export interface AgendaClientGroup {
  clientId: string;
  clientName: string;
  passbookId: string;
  passbookName?: string;
  backgroundColor?: string;
  campaigns: AgendaCampaignRow[];
  nextRunAt: Date | null;
  nextCampaignName: string;
  nextRunLabel: string;
  nextChannel: string;
}

function enrichCampaign(c: AgendaCampaign, now: Date): AgendaCampaignRow {
  const nextRunAt = computeNextRunAt(c, now);
  const meta = campaignDispatchMeta(c);
  const dispatchParts = [meta.dateLabel, meta.timeLabel].filter(Boolean);
  return {
    ...c,
    nextRunAt,
    nextRunLabel: formatNextRunLabel(nextRunAt),
    dispatchLabel: dispatchParts.join(' · ') || '—',
  };
}

// O nome do canal é o mesmo do resto do painel (marketing-shared).
export { channelLabel };

export function agendaClientKey(campaign: Pick<AgendaCampaign, 'clientId' | 'passbookId' | 'id_passbook'>): string {
  if (campaign.clientId) return campaign.clientId;
  const pb = campaign.passbookId || campaign.id_passbook;
  return pb ? `passbook:${pb}` : 'unknown';
}

export interface AgendaFilterOptions {
  search: string;
  channel: string;
  frequency: string;
  window: AgendaWindow;
  includeInactive: boolean;
  now?: Date;
}

function matchesFilters(row: AgendaCampaignRow, opts: AgendaFilterOptions): boolean {
  // Expiradas nunca entram na agenda (mesmo em janela "todas").
  if (isCampaignExpired(row, opts.now)) return false;
  if (!opts.includeInactive && row.active === false) return false;
  if (opts.channel !== 'all' && (row.tipo_disparo ?? '') !== opts.channel) return false;
  if (opts.frequency !== 'all' && String(row.frequencia ?? '') !== opts.frequency) return false;
  if (!isNextRunInWindow(row.nextRunAt, opts.window, opts.now)) return false;

  const q = opts.search.trim().toLowerCase();
  if (!q) return true;
  const hay = [row.clientName, row.name, row.message, row.description, row.passbookName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/** Enriquece, filtra e agrupa campanhas por cliente, ordenado pelo próximo disparo. */
export function groupAgendaByClient(
  campaigns: AgendaCampaign[],
  opts: AgendaFilterOptions,
): AgendaClientGroup[] {
  const now = opts.now ?? new Date();
  const enriched = campaigns
    .map((c) => enrichCampaign(c, now))
    .filter((c) => matchesFilters(c, { ...opts, now }));

  const byClient = new Map<string, AgendaClientGroup>();

  for (const row of enriched) {
    const key = agendaClientKey(row);
    let group = byClient.get(key);
    if (!group) {
      group = {
        clientId: key,
        clientName: row.clientName || 'Cliente desconhecido',
        passbookId: row.passbookId || row.id_passbook || '',
        passbookName: row.passbookName,
        backgroundColor: row.backgroundColor,
        campaigns: [],
        nextRunAt: null,
        nextCampaignName: '',
        nextRunLabel: 'Sem próximo disparo',
        nextChannel: '',
      };
      byClient.set(key, group);
    }
    group.campaigns.push(row);
  }

  const groups = Array.from(byClient.values()).map((group) => {
    group.campaigns.sort((a, b) => {
      const ta = a.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
      const tb = b.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
      return ta - tb;
    });
    const nearest = group.campaigns[0];
    if (nearest) {
      group.nextRunAt = nearest.nextRunAt;
      group.nextCampaignName = nearest.name || 'Campanha';
      group.nextRunLabel = nearest.nextRunLabel;
      group.nextChannel = channelLabel(nearest.tipo_disparo);
      group.passbookId = nearest.passbookId || nearest.id_passbook || group.passbookId;
      group.backgroundColor = nearest.backgroundColor ?? group.backgroundColor;
      group.passbookName = nearest.passbookName ?? group.passbookName;
    }
    return group;
  });

  groups.sort((a, b) => {
    const ta = a.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
    const tb = b.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return a.clientName.localeCompare(b.clientName, 'pt-BR');
  });

  return groups;
}

/** Resolve o nome do cliente sem aplicar filtros de janela/canal (para título da página). */
export function resolveAgendaClientName(
  campaigns: AgendaCampaign[],
  clientId: string,
): string | undefined {
  const hit = campaigns.find((c) => agendaClientKey(c) === clientId);
  return hit?.clientName;
}

export function filterClientCampaigns(
  campaigns: AgendaCampaign[],
  clientId: string,
  opts: AgendaFilterOptions,
): AgendaCampaignRow[] {
  const now = opts.now ?? new Date();
  return campaigns
    .filter((c) => agendaClientKey(c) === clientId)
    .map((c) => enrichCampaign(c, now))
    .filter((c) => matchesFilters(c, { ...opts, now }))
    .sort((a, b) => {
      const ta = a.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
      const tb = b.nextRunAt?.getTime() ?? Number.POSITIVE_INFINITY;
      return ta - tb;
    });
}
